import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { filter } from 'rxjs/operators';
import { LangService, Langue } from './services/lang.service';
import { AuthService } from './services/auth.service';
import { ContenuSiteService } from './services/contenu-site.service';

@Component({
  selector: 'app-root',
  template: `
    <nav>
      <a routerLink="/" class="logo" (click)="fermerMenu()">
        <span class="logo-cercle">FIS</span>
        <span class="logo-texte">Festival International de Sousse</span>
      </a>

      <button class="hamburger" (click)="menuOuvert = !menuOuvert" [class.ouvert]="menuOuvert" aria-label="Menu">
        <span></span><span></span><span></span>
      </button>

      <div class="nav-liens" [class.ouvert]="menuOuvert">
        <a routerLink="/" (click)="fermerMenu()">{{ 'nav.accueil' | traduire }}</a>
        <a routerLink="/" fragment="programme" (click)="fermerMenu()">{{ 'nav.programme' | traduire }}</a>
        <a routerLink="/" fragment="artistes" (click)="fermerMenu()">{{ 'nav.artistes' | traduire }}</a>
        <a routerLink="/" fragment="archives" (click)="fermerMenu()">{{ 'nav.archives' | traduire }}</a>
        <a routerLink="/" fragment="theatre" (click)="fermerMenu()">{{ 'nav.theatre' | traduire }}</a>
        <a routerLink="/" fragment="decouvrir" (click)="fermerMenu()">{{ 'nav.decouvrir' | traduire }}</a>

        <div class="lang-switch">
          <button [class.actif]="langueActuelle==='fr'" (click)="changerLangue('fr')">FR</button>
          <button [class.actif]="langueActuelle==='en'" (click)="changerLangue('en')">EN</button>
          <button [class.actif]="langueActuelle==='ar'" (click)="changerLangue('ar')">AR</button>
        </div>

        <a (click)="allerVersEspaceAdmin(); fermerMenu()" class="admin-link" style="cursor:pointer;">{{ 'nav.admin' | traduire }}</a>
      </div>
    </nav>

    <router-outlet></router-outlet>
    <app-assistant></app-assistant>
  `,
  styles: [`
    nav {
      flex-wrap: wrap;
      position: sticky;
      top: 0;
      z-index: 100;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 10px;
      text-transform: none;
    }

    .logo-cercle {
      width: 34px;
      height: 34px;
      border: 1px solid var(--gold);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--serif);
      color: var(--gold);
      font-size: 11px;
      flex-shrink: 0;
    }

    .logo-texte {
      font-family: var(--serif);
      color: var(--cream);
      font-size: 15px;
      white-space: nowrap;
    }

    .lang-switch { display: flex; gap: 4px; }
    .lang-switch button {
      background: none; border: 1px solid var(--line); color: var(--stone);
      padding: 5px 10px; border-radius: 999px; font-size: 11px; cursor: pointer;
    }
    .lang-switch button.actif { background: var(--gold); color: var(--void); border-color: var(--gold); }

    .hamburger {
      display: none;
      flex-direction: column;
      justify-content: center;
      gap: 5px;
      width: 32px;
      height: 32px;
      background: none;
      border: none;
      cursor: pointer;
      margin-left: auto;
    }

    .hamburger span {
      display: block;
      height: 2px;
      background: var(--gold-bright);
      border-radius: 2px;
      transition: transform .2s ease, opacity .2s ease;
    }

    .hamburger.ouvert span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    .hamburger.ouvert span:nth-child(2) { opacity: 0; }
    .hamburger.ouvert span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

    .nav-liens {
      display: flex;
      align-items: center;
      gap: 28px;
      flex-wrap: wrap;
    }

    /* ---------- mobile : nav repliee en menu deroulant ---------- */
    @media (max-width: 900px) {
      .logo-texte { font-size: 13px; }

      .hamburger { display: flex; }

      .nav-liens {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        background: var(--navy);
        border-bottom: 1px solid var(--line);
        padding: 12px 24px 20px;
        z-index: 200;
      }

      .nav-liens.ouvert { display: flex; }

      .nav-liens a { padding: 10px 0; width: 100%; }

      .lang-switch { margin: 10px 0; }

      .admin-link { margin-left: 0 !important; opacity: 1; }
    }
  `]
})
export class AppComponent implements OnInit {
  title = 'festival-sousse-frontend';
  langueActuelle: Langue = 'fr';
  menuOuvert = false;

  constructor(
    private langService: LangService,
    private authService: AuthService,
    private router: Router,
    private contenuSiteService: ContenuSiteService,
    private viewportScroller: ViewportScroller
  ) {
    this.langService.langue$.subscribe(l => this.langueActuelle = l);

    // Defile manuellement vers la section demandee (#programme, #artistes...) une fois
    // que la navigation est terminee et que le DOM a fini de se reconstruire. Necessaire
    // car onSameUrlNavigation: 'reload' recharge tout AccueilComponent a chaque clic sur
    // un lien du menu (meme depuis l'accueil), et le defilement automatique d'Angular
    // (anchorScrolling) intervient parfois trop tot, avant que la section n'existe dans
    // le DOM - ce qui causait le comportement "un coup ca marche, un coup non" sur mobile.
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const fragment = this.router.parseUrl(this.router.url).fragment;
      if (fragment) {
        setTimeout(() => {
          this.viewportScroller.scrollToAnchor(fragment);
        }, 150);
      } else {
        setTimeout(() => this.viewportScroller.scrollToPosition([0, 0]), 0);
      }
    });
  }

  ngOnInit(): void {
    this.contenuSiteService.charger().subscribe();
  }

  fermerMenu(): void {
    this.menuOuvert = false;
  }

  changerLangue(langue: Langue): void {
    this.langService.changerLangue(langue);
  }

  // Si l'admin est deja connecte (session encore valide), on l'emmene directement
  // au tableau de bord sans repasser par le formulaire de connexion.
  allerVersEspaceAdmin(): void {
    if (this.authService.estConnecte()) {
      this.router.navigate(['/admin/dashboard']);
    } else {
      this.router.navigate(['/admin/login']);
    }
  }
}
